import React, { useState, useEffect, useRef } from "react";
import { CustomModal } from "../../../components/CustomModel/CustomModal";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import useAxios from "../../../utils/useAxios";
import TournamentPrintableTable from "./TournamentPrintableTable";
import {
    copyToClipboard,
    exportWorkbook,
    buildScheduleDataset,
    buildGroupsDataset,
    buildParticipantsDataset,
    buildResultsDataset,
    buildBracketsDataset
} from "./exportUtils";
import { registerPdfFonts } from "./pdfFonts";
import { px2mm, px2pt, lineHeightMm } from "./pdfKit";
import { PRINT_THEME as PDF_T } from "./printTheme";
import { measureToc, drawToc } from "./pdfToc";
import { drawFooter } from "./pdfFooter";
import { preloadBlockAssets, measureBlockPage, drawBlockPage, MARGIN_MM } from "./pdfPageComposer";

const BLOCKS = {
    schedule:     { label: "Schedule",     icon: "calendar",    tab: "schedule",  param: "stage", arrayKey: "schedule_stages" },
    // Groups and brackets read as one "Draw" block here (same label/order
    // as the tournament page's own merged tab - see TournamentDrawStages.js),
    // with sub-stages listed in that tab's own combined order
    // (stagesInfo.draws_merged). Each sub-stage still carries its own
    // `.kind` ("groups" vs "draws") - handleTabularExport branches on that
    // to call the right build*Dataset function underneath.
    draws:        { label: "Draw",         icon: "git-commit",  arrayKey: "draws_merged" },
    participants: { label: "Participants", icon: "users",       tab: "players" },
    results:      { label: "Results",      icon: "bar-chart-2", tab: "rating" },
};

const TabularExportButtons = ({ blockKey, subItemVal, handleTabularExport, loadingKey }) => {
    const subKey = typeof subItemVal === 'object' ? `${subItemVal.stage || subItemVal.id || ''}_${subItemVal.tab || ''}` : (subItemVal || 1);
    const isPdfLoading = loadingKey === `tabular-${blockKey}-${subKey}-pdf`;
    const isPngLoading = loadingKey === `tabular-${blockKey}-${subKey}-png`;
    const isExcelLoading = loadingKey === `tabular-${blockKey}-${subKey}-excel`;
    const isClipLoading = loadingKey === `tabular-${blockKey}-${subKey}-clipboard`;
    const isBusy = !!loadingKey;

    return (
        <div style={{ marginLeft: "20px", marginTop: "8px", marginBottom: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                <div
                    className={`admin-card ${isPdfLoading ? "selected" : "editable"}`}
                    style={{ cursor: isBusy ? "wait" : "pointer", padding: "8px 10px", gridTemplateColumns: "18px 1fr auto", margin: 0 }}
                    onClick={(e) => {
                        e.preventDefault();
                        if (!isBusy) handleTabularExport(blockKey, subItemVal, 'pdf');
                    }}
                >
                    <div><FeatherIcon icon="file-text" size={15} /></div>
                    <div style={{ fontSize: "12px", fontWeight: "500" }}>{t("PDF Table")}</div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {isPdfLoading ? <FeatherIcon icon="loader" className="spin" size={14} /> : <FeatherIcon icon="download" size={14} />}
                    </div>
                </div>

                <div
                    className={`admin-card ${isPngLoading ? "selected" : "editable"}`}
                    style={{ cursor: isBusy ? "wait" : "pointer", padding: "8px 10px", gridTemplateColumns: "18px 1fr auto", margin: 0 }}
                    onClick={(e) => {
                        e.preventDefault();
                        if (!isBusy) handleTabularExport(blockKey, subItemVal, 'png');
                    }}
                >
                    <div><FeatherIcon icon="image" size={15} /></div>
                    <div style={{ fontSize: "12px", fontWeight: "500" }}>{t("PNG Image")}</div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {isPngLoading ? <FeatherIcon icon="loader" className="spin" size={14} /> : <FeatherIcon icon="download" size={14} />}
                    </div>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                <div
                    className={`admin-card ${isExcelLoading ? "selected" : "editable"}`}
                    style={{ cursor: isBusy ? "wait" : "pointer", padding: "8px 10px", gridTemplateColumns: "18px 1fr auto", margin: 0 }}
                    onClick={(e) => {
                        e.preventDefault();
                        if (!isBusy) handleTabularExport(blockKey, subItemVal, 'excel');
                    }}
                >
                    <div><FeatherIcon icon="grid" size={15} /></div>
                    <div style={{ fontSize: "12px", fontWeight: "500" }}>{t("Excel (.xlsx)")}</div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {isExcelLoading ? <FeatherIcon icon="loader" className="spin" size={14} /> : <FeatherIcon icon="download" size={14} />}
                    </div>
                </div>

                <div
                    className={`admin-card ${isClipLoading ? "selected" : "editable"}`}
                    style={{ cursor: isBusy ? "wait" : "pointer", padding: "8px 10px", gridTemplateColumns: "18px 1fr auto", margin: 0 }}
                    onClick={(e) => {
                        e.preventDefault();
                        if (!isBusy) handleTabularExport(blockKey, subItemVal, 'clipboard');
                    }}
                >
                    <div><FeatherIcon icon="clipboard" size={15} /></div>
                    <div style={{ fontSize: "12px", fontWeight: "500" }}>{t("Copy Table")}</div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {isClipLoading ? <FeatherIcon icon="loader" className="spin" size={14} /> : <FeatherIcon icon="copy" size={14} />}
                    </div>
                </div>
            </div>
        </div>
    );
};

const TournamentExportModal = ({ isOpen, onClose, tournamentId, tournament }) => {
    const { i18n } = useTranslation();
    const api = useAxios();
    const [loadingKey, setLoadingKey] = useState(null);
    const [stagesInfo, setStagesInfo] = useState(null);
    // Which top-level blocks (Schedule/Draw/Participants/Results) are open -
    // collapsed by default. A Set (not the single "one block open at a time"
    // scalar this used to be) since several can stay open together.
    const [expandedBlocks, setExpandedBlocks] = useState(() => new Set());

    // Printable table state for Tabular mode (offscreen rendering)
    const [printableData, setPrintableData] = useState(null);
    const printableContainerRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !tournamentId) return;
        let isMounted = true;

        const loadStages = async () => {
            try {
                const [sch, grp, drw] = await Promise.all([
                    api.get(`/api/v1.0/tournaments/${tournamentId}/schedules/stages/`).catch(() => ({ data: [] })),
                    api.get(`/api/v1.0/tournaments/${tournamentId}/group-stage/`).catch(() => ({ data: [] })),
                    api.get(`/api/v1.0/tournaments/${tournamentId}/draw-stage/`).catch(() => ({ data: [] })),
                ]);

                if (!isMounted) return;

                const scheduleStages = sch.data?.results || (Array.isArray(sch.data) ? sch.data : []);
                const groupStages = grp.data?.results || (Array.isArray(grp.data) ? grp.data : []);
                const rawDrawStages = drw.data?.results || (Array.isArray(drw.data) ? drw.data : []);

                const drawStages = [];
                rawDrawStages.forEach(stg => {
                    const stgName = stg.name || `${t("Stage")} ${stg.order || stg.id}`;
                    if (stg.main_draw) {
                        drawStages.push({ stage: stg.id, tab: "main", label: `${stgName} - ${t("Main draw")}`, draw_number: stg.draw_number });
                    }
                    if (stg.qualification) {
                        drawStages.push({ stage: stg.id, tab: "qualification", label: `${stgName} - ${t("Qualification")}`, draw_number: stg.draw_number });
                    }
                    if (!stg.main_draw && !stg.qualification) {
                        drawStages.push({ stage: stg.id, tab: "main", label: stgName, draw_number: stg.draw_number });
                    }
                });

                // Same merge TournamentDrawStages.js's own live "Жеребкування"
                // tab does (group_stages ordered by .order, draw_stages by
                // .draw_number, interleaved) - the export picker's "Draw"
                // entry lists sub-stages in that same combined order instead
                // of as two separate top-level blocks, since the two haven't
                // been separate tabs on the tournament page itself for a
                // while now.
                const drawsMerged = [
                    ...groupStages.map(s => ({ ...s, kind: "groups", pos: s.order || 0 })),
                    ...drawStages.map(s => ({ ...s, kind: "draws", pos: s.draw_number || 0 })),
                ].sort((a, b) => a.pos - b.pos);

                setStagesInfo({
                    schedule_stages: scheduleStages,
                    group_stages: groupStages,
                    draw_stages: drawStages,
                    draws_merged: drawsMerged
                });
            } catch (e) {
                console.error("Failed to load stages for export:", e);
            }
        };

        loadStages();

        return () => {
            isMounted = false;
            setLoadingKey(null);
            setExpandedBlocks(new Set());
            setPrintableData(null);
        };
    }, [isOpen, tournamentId]);

    // Read-only fetch, no dataset/format branching - shared by
    // handleInteractivePdfExport below to pull the same {stageName,
    // tableData} pair handleTabularExport's own inline branches already
    // compute per format. Kept as its own separate function rather than
    // factored out of handleTabularExport itself: that function is the
    // already-shipping, individually-tested per-block PDF/PNG/Excel/
    // Clipboard export, and reshaping its working branches to share code
    // risks a regression there for the sake of this new, additive feature.
    const fetchStageTableData = async (blockKey, subItemVal) => {
        const grade = tournament?.grade || 1;

        if (blockKey === "schedule") {
            const stages = stagesInfo?.schedule_stages || [];
            const targetStage = typeof subItemVal === 'object' ? subItemVal : (stages.find(s => s.id === subItemVal) || stages[0] || { id: subItemVal });
            const stageId = (targetStage && targetStage.id) || (typeof subItemVal === 'number' ? subItemVal : stages[0]?.id);
            const stageName = (targetStage?.name && !/^\d+$/.test(targetStage.name.trim())) ? targetStage.name : (targetStage?.date || "");
            const res = await api.get(`/api/v1.0/tournaments/${tournamentId}/schedules/stages/${stageId}/`);
            return { stageName, tableData: res.data };
        }

        if (blockKey === "draws" && subItemVal?.kind === "groups") {
            const stageId = subItemVal.id;
            const stageName = (subItemVal?.name && !/^\d+$/.test(String(subItemVal.name).trim())) ? subItemVal.name : "";
            const res = await api.get(`/api/v1.0/group-stage/${stageId}/groups/?limit=100`);
            const rawGroups = res.data?.results || (Array.isArray(res.data) ? res.data : []);
            const tableData = rawGroups.map(group => {
                const count = group.number_players || (group.participants?.length || 4);
                const players = Array.from({ length: count }, (_, i) => group.participants?.[i] || group.players?.[i] || null);
                return { ...group, players, matches: group.matches || [] };
            });
            return { stageName, tableData };
        }

        if (blockKey === "participants") {
            const participantsOrdering = grade > 1 ? "-general-rating" : "-rating";
            const url = grade > 1
                ? `/api/v1.0/tournaments/${tournamentId}/teams/?limit=300&ordering=${participantsOrdering}`
                : `/api/v1.0/tournaments/${tournamentId}/players/?limit=300&ordering=${participantsOrdering}`;
            const res = await api.get(url);
            return { stageName: "", tableData: res.data?.results || (Array.isArray(res.data) ? res.data : []) };
        }

        if (blockKey === "results") {
            const res = await api.get(`/api/v1.0/tournaments/${tournamentId}/players-rating/?limit=300&grade=${grade}&ordering=-tournament_score`);
            return { stageName: "", tableData: res.data?.results || (Array.isArray(res.data) ? res.data : []) };
        }

        if (blockKey === "draws" && subItemVal?.kind === "draws") {
            const stageId = subItemVal.stage || subItemVal.id;
            const stageName = (subItemVal.label && !/^\d+$/.test(String(subItemVal.label).trim())) ? subItemVal.label : "";
            const res = await api.get(`/api/v1.0/tournaments/${tournamentId}/draw-stage/${stageId}/draws/`);
            return { stageName, tableData: res.data?.results || (Array.isArray(res.data) ? res.data : []) };
        }

        return { stageName: "", tableData: null };
    };

    // Combines every section (Participants / every Draw sub-stage / every
    // Schedule sub-stage / Results) into ONE PDF instead of the one-file-
    // per-block downloads above, with a linked Contents page and a PDF
    // outline (bookmark sidebar in Adobe/Chrome/Preview/...).
    //
    // Every page is real vector text now (pdfHeader/pdfFooter/pdf*Table/
    // pdfBracketTree/pdfToc, via pdfPageComposer) - PT Sans is embedded as
    // a real Unicode TTF (see pdfFonts.js) specifically because jsPDF's own
    // 14 built-in fonts only cover WinAnsi/Latin-1 and would corrupt every
    // Cyrillic character. The Contents page's row "links" are now just
    // pdf.link() rectangles at the mm coordinates drawToc() itself just
    // drew at - no DOM/pixel measurement involved anywhere in this flow.
    //
    // Bookmark TITLES are the one exception that still goes through
    // jsPDF's own text encoding (its outline plugin has no font/glyph
    // concept - a bookmark title is a PDF string object, escaped by
    // jsPDF's own single-byte pdfEscape no matter what's drawn on the
    // page). So bookmark labels deliberately use each block's untranslated
    // English key (BLOCKS.x.label) instead of the Ukrainian t(...) text
    // used everywhere else here - guaranteed to render correctly in every
    // viewer's sidebar, at the cost of being English-only there while the
    // actual Contents page and page headers stay fully localized.
    const drawBackLink = (pdf, x, y, maxWidthMm) => {
        const fontSizePt = px2pt(12);
        const text = `← ${t("Contents")}`;
        pdf.setFont(pdf.__pdfFontName || "PTSans", "bold");
        pdf.setFontSize(fontSizePt);
        const w = pdf.getTextWidth(text);
        const h = lineHeightMm(fontSizePt, 1.3) + px2mm(12);
        pdf.setTextColor(PDF_T.darkerGreen);
        pdf.text(text, x, y + h / 2 + fontSizePt * 0.352778 * 0.32);
        return { nextY: y + h, rect: { x, y, w, h } };
    };

    const handleInteractivePdfExport = async () => {
        if (!stagesInfo || loadingKey) return;
        setLoadingKey('interactive-pdf');
        try {
            const grade = tournament?.grade || 1;

            const categoryDefs = [
                {
                    label: t(BLOCKS.participants.label),
                    bookmark: BLOCKS.participants.label,
                    items: [{ blockKey: 'participants', subItemVal: 1, label: t(BLOCKS.participants.label) }]
                },
                {
                    label: t(BLOCKS.draws.label),
                    bookmark: BLOCKS.draws.label,
                    items: (stagesInfo.draws_merged || []).map((item, idx) => ({
                        blockKey: 'draws',
                        subItemVal: item,
                        label: item.kind === 'groups'
                            ? ((item.name && !/^\d+$/.test(String(item.name).trim())) ? item.name : `${t(BLOCKS.draws.label)} ${idx + 1}`)
                            : (item.label || `${t(BLOCKS.draws.label)} ${idx + 1}`),
                    }))
                },
                {
                    label: t(BLOCKS.schedule.label),
                    bookmark: BLOCKS.schedule.label,
                    items: (stagesInfo.schedule_stages || []).map((s, idx) => ({
                        blockKey: 'schedule',
                        subItemVal: s,
                        label: (s.name && !/^\d+$/.test(String(s.name).trim())) ? s.name : (s.date || `${t(BLOCKS.schedule.label)} ${idx + 1}`),
                    }))
                },
                {
                    label: t(BLOCKS.results.label),
                    bookmark: BLOCKS.results.label,
                    items: [{ blockKey: 'results', subItemVal: 1, label: t(BLOCKS.results.label) }]
                },
            ].filter(cat => cat.items.length > 0);

            // Pass 1: fetch + measure every section, in reading order.
            const sections = [];
            for (const cat of categoryDefs) {
                for (const item of cat.items) {
                    const printableKind = item.blockKey === "draws" ? (item.subItemVal?.kind === "groups" ? "groups" : "brackets") : item.blockKey;
                    let stageName = "", tableData = null;
                    try {
                        ({ stageName, tableData } = await fetchStageTableData(item.blockKey, item.subItemVal));
                    } catch (e) {
                        continue;
                    }
                    if (!tableData || (Array.isArray(tableData) && tableData.length === 0)) continue;

                    const assets = await preloadBlockAssets(printableKind, tournament, tableData, grade);
                    const { contentWidthMm, contentHeightMm } = await measureBlockPage(printableKind, tournament, stageName, tableData, grade);
                    const backLinkH = lineHeightMm(px2pt(12), 1.3) + px2mm(12);

                    sections.push({
                        categoryLabel: cat.label,
                        bookmarkLabel: cat.items.length > 1 ? `${cat.bookmark} ${cat.items.indexOf(item) + 1}` : cat.bookmark,
                        label: item.label,
                        printableKind, stageName, tableData, assets,
                        contentWidthMm, contentHeightMm: contentHeightMm + backLinkH,
                    });
                }
            }

            if (!sections.length) {
                toast.error(t("No data to export"));
                return;
            }

            // Group the captured sections back into their categories for the
            // Contents page - by consecutive categoryLabel, not by re-walking
            // categoryDefs: sections here already skips any sub-stage that
            // had no data, so it and categoryDefs don't line up 1:1 by index.
            const tocCategories = [];
            sections.forEach((sec, idx) => {
                const lastGroup = tocCategories[tocCategories.length - 1];
                const group = (lastGroup && lastGroup.label === sec.categoryLabel) ? lastGroup : (() => {
                    const g = { label: sec.categoryLabel, items: [] };
                    tocCategories.push(g);
                    return g;
                })();
                group.items.push({ key: idx, label: sec.label });
            });

            // Pass 2: assemble the combined PDF. Every page is sized to its
            // own content (same [w, h] custom-format approach the single-
            // block PDF export already uses) rather than a shared fixed
            // page size - a bracket sheet and a 3-column group table have
            // nothing in common dimension-wise.
            const toPageSize = (widthMm, heightMm) => {
                const w = widthMm + MARGIN_MM * 2;
                const h = heightMm + MARGIN_MM * 2;
                return { w, h, orientation: w >= h ? 'landscape' : 'portrait' };
            };

            const TOC_WIDTH_MM = px2mm(900);
            const tocHeightMm = measureToc(tocCategories);
            const tocSize = toPageSize(TOC_WIDTH_MM, tocHeightMm);
            // Dynamic import: jspdf is ~325KB of this bundle's JS for a
            // feature most visits never touch - loaded on demand, right
            // when the user actually exports, instead of shipping it to
            // every page load.
            const { default: jsPDF } = await import("jspdf");
            const pdf = new jsPDF({ unit: 'mm', format: [tocSize.w, tocSize.h], orientation: tocSize.orientation });
            await registerPdfFonts(pdf);

            const tocLogo = sections[0]?.assets?.logoDataUrl || null;
            const { nextY: tocContentY, itemRects } = drawToc(pdf, MARGIN_MM, MARGIN_MM, tournament, tocCategories, TOC_WIDTH_MM, tocLogo);
            drawFooter(pdf, MARGIN_MM, tocContentY, TOC_WIDTH_MM);
            Object.entries(itemRects).forEach(([key, rect]) => {
                pdf.link(rect.x, rect.y, rect.w, rect.h, { pageNumber: Number(key) + 2 });
            });
            pdf.outline.add(null, "Contents", { pageNumber: 1 });

            let lastCategoryLabel = null;
            let lastCategoryNode = null;

            sections.forEach((sec, idx) => {
                const pageNum = idx + 2;
                const size = toPageSize(sec.contentWidthMm, sec.contentHeightMm);
                pdf.addPage([size.w, size.h], size.orientation);

                const { nextY: afterBackLink, rect: backLinkRect } = drawBackLink(pdf, MARGIN_MM, MARGIN_MM, sec.contentWidthMm);
                drawBlockPage(pdf, MARGIN_MM, afterBackLink, sec.printableKind, tournament, sec.stageName, sec.tableData, grade, i18n, sec.contentWidthMm, sec.assets);
                pdf.link(backLinkRect.x, backLinkRect.y, backLinkRect.w, backLinkRect.h, { pageNumber: 1 });

                if (sec.categoryLabel !== lastCategoryLabel) {
                    lastCategoryLabel = sec.categoryLabel;
                    const catDef = categoryDefs.find(c => c.label === sec.categoryLabel);
                    lastCategoryNode = (catDef?.items?.length > 1)
                        ? pdf.outline.add(null, catDef?.bookmark || sec.categoryLabel, { pageNumber: pageNum })
                        : null;
                }
                pdf.outline.add(lastCategoryNode, sec.bookmarkLabel, { pageNumber: pageNum });
            });

            const fileName = `${tournament?.name || tournamentId}_full`.replace(/[\s\/:*?"<>|]/g, "_");
            pdf.save(`${fileName}.pdf`);
            toast.success(`${t("Interactive PDF")} ${t("saved!")}`);
        } catch (e) {
            console.error("Interactive PDF export error:", e);
            toast.error(t("Export failed"));
        } finally {
            setLoadingKey(null);
        }
    };

    const handleTabularExport = async (blockKey, subItemVal, format = 'excel') => {
        const subKey = typeof subItemVal === 'object' ? `${subItemVal.stage || subItemVal.id || ''}_${subItemVal.tab || ''}` : (subItemVal || 1);
        const key = `tabular-${blockKey}-${subKey}-${format}`;
        setLoadingKey(key);
        // TournamentPrintableTable.js's own renderer switch still keys off
        // the original flat block types ("groups"/"brackets"/"schedule"/...),
        // not the merged "draws" picker key (BLOCKS.draws above) - passing
        // the raw blockKey straight through made every group/bracket PNG/PDF
        // export match none of that switch's conditions and render with no
        // data at all (just the header/footer). Resolve which flat type
        // this particular sub-stage actually is instead.
        const printableKind = blockKey === "draws" ? (subItemVal?.kind === "groups" ? "groups" : "brackets") : blockKey;

        try {
            let stageName = "";
            let dataset = null;
            let tableData = null;
            const grade = tournament?.grade || 1;

            if (blockKey === "schedule") {
                let stages = stagesInfo?.schedule_stages || [];
                if (!stages.length) {
                    try {
                        const schRes = await api.get(`/api/v1.0/tournaments/${tournamentId}/schedules/stages/`);
                        stages = schRes.data?.results || schRes.data || [];
                    } catch (e) {}
                }
                const targetStage = typeof subItemVal === 'object' ? subItemVal : (stages.find(s => s.id === subItemVal) || stages[0] || { id: subItemVal });
                const stageId = (targetStage && targetStage.id) || (typeof subItemVal === 'number' ? subItemVal : stages[0]?.id);
                stageName = (targetStage?.name && !/^\d+$/.test(targetStage.name.trim())) ? targetStage.name : (targetStage?.date || "");

                const res = await api.get(`/api/v1.0/tournaments/${tournamentId}/schedules/stages/${stageId}/`);
                tableData = res.data;
                dataset = buildScheduleDataset(tournament, stageName, tableData, grade, i18n);
            }
            else if (blockKey === "draws" && subItemVal?.kind === "groups") {
                let stages = stagesInfo?.group_stages || [];
                if (!stages.length) {
                    try {
                        const grpRes = await api.get(`/api/v1.0/tournaments/${tournamentId}/group-stage/`);
                        stages = grpRes.data?.results || grpRes.data || [];
                    } catch (e) {}
                }
                const targetStage = typeof subItemVal === 'object' ? subItemVal : (stages.find(s => s.id === subItemVal) || stages[0] || { id: subItemVal });
                const stageId = (targetStage && targetStage.id) || (typeof subItemVal === 'number' ? subItemVal : stages[0]?.id);
                stageName = (targetStage?.name && !/^\d+$/.test(targetStage.name.trim())) ? targetStage.name : "";

                if (!stageId) {
                    toast.error(t("No group stage found"));
                    return;
                }

                const res = await api.get(`/api/v1.0/group-stage/${stageId}/groups/?limit=100`);
                const rawGroups = res.data?.results || (Array.isArray(res.data) ? res.data : []);
                const mappedGroups = rawGroups.map(group => {
                    const count = group.number_players || (group.participants?.length || 4);
                    const players = Array.from({ length: count }, (_, i) => group.participants?.[i] || group.players?.[i] || null);
                    return {
                        ...group,
                        players,
                        matches: group.matches || []
                    };
                });
                tableData = mappedGroups;
                dataset = buildGroupsDataset(tournament, stageName, tableData, grade);
            }
            else if (blockKey === "participants") {
                // Same default sort as the live "Участники" tab (TournamentPlayers.js) -
                // without this the backend's own default (id/creation order) doesn't
                // match what the admin sees on screen, so the exported "№" numbering
                // looked scrambled relative to real ranking.
                const participantsOrdering = grade > 1 ? "-general-rating" : "-rating";
                const url = grade > 1
                    ? `/api/v1.0/tournaments/${tournamentId}/teams/?limit=300&ordering=${participantsOrdering}`
                    : `/api/v1.0/tournaments/${tournamentId}/players/?limit=300&ordering=${participantsOrdering}`;
                const res = await api.get(url);
                tableData = res.data?.results || (Array.isArray(res.data) ? res.data : []);
                stageName = "";
                dataset = buildParticipantsDataset(tournament, tableData, grade);
            }
            else if (blockKey === "results") {
                // Same default sort as the live "Рейтинг" tab (TournamentRatingPlayers.js).
                const res = await api.get(`/api/v1.0/tournaments/${tournamentId}/players-rating/?limit=300&grade=${grade}&ordering=-tournament_score`);
                tableData = res.data?.results || (Array.isArray(res.data) ? res.data : []);
                stageName = "";
                dataset = buildResultsDataset(tournament, tableData, grade);
            }
            else if (blockKey === "draws" && subItemVal?.kind === "draws") {
                let stageId = null;
                if (typeof subItemVal === 'object') {
                    stageId = subItemVal.stage || subItemVal.id;
                    stageName = (subItemVal.label || subItemVal.name || "");
                } else {
                    const stages = stagesInfo?.draw_stages || [];
                    const found = stages.find(s => (s.stage || s.id) === subItemVal);
                    stageId = found?.stage || found?.id || subItemVal;
                    stageName = (found?.label || found?.name || "");
                }

                if (!stageId) {
                    try {
                        const stgRes = await api.get(`/api/v1.0/tournaments/${tournamentId}/draw-stage/`);
                        const stgs = stgRes.data?.results || stgRes.data || [];
                        if (stgs.length) stageId = stgs[0].id;
                    } catch (e) {}
                }

                if (stageName && /^\d+$/.test(stageName.trim())) {
                    stageName = "";
                }

                const drwRes = await api.get(`/api/v1.0/tournaments/${tournamentId}/draw-stage/${stageId}/draws/`);
                tableData = drwRes.data?.results || (Array.isArray(drwRes.data) ? drwRes.data : []);
                dataset = buildBracketsDataset(tournament, stageName, tableData, grade, i18n);
            }

            if (!dataset) {
                toast.error(t("No data to export"));
                return;
            }

            if (format === 'excel') {
                const fileName = `${t(BLOCKS[blockKey].label)}_${tournament?.name || tournamentId}`.replace(/[\s\/:*?"<>|]/g, "_");
                const sheets = (dataset.sheets && Object.keys(dataset.sheets).length > 0) ? dataset.sheets : { [stageName || "Sheet1"]: dataset.aoa || [["No data"]] };
                await exportWorkbook(sheets, fileName);
            }
            else if (format === 'clipboard') {
                await copyToClipboard(dataset.tsv, dataset.html);
            }
            else if (format === 'png') {
                setPrintableData({
                    blockKey: printableKind,
                    title: t(BLOCKS[blockKey].label),
                    subTitle: stageName,
                    data: tableData,
                    grade
                });

                // Wait for state update and DOM mount
                await new Promise(r => setTimeout(r, 200));

                const el = printableContainerRef.current;
                if (!el) throw new Error("Printable container element not found");

                // Dynamic import - see the "Interactive PDF" jsPDF import
                // above for why.
                const { domToPng } = await import("modern-screenshot");
                const dataUrl = await domToPng(el, {
                    scale: 2,
                    backgroundColor: '#ffffff',
                    width: el.scrollWidth || 1000,
                    height: el.scrollHeight,
                });

                const fileName = `${t(BLOCKS[blockKey].label)}_${tournament?.name || tournamentId}`.replace(/[\s\/:*?"<>|]/g, "_");
                const link = document.createElement("a");
                link.download = `${fileName}.png`;
                link.href = dataUrl;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success(`${t(BLOCKS[blockKey].label)} ${t("saved!")}`);
            }
            else if (format === 'pdf') {
                // Vector PDF - no DOM mount/screenshot involved (see
                // pdfPageComposer.js). Real text + graphics via jsPDF, sized
                // to its own content just like the old screenshot path was.
                const assets = await preloadBlockAssets(printableKind, tournament, tableData, grade);
                const { contentWidthMm, contentHeightMm } = await measureBlockPage(printableKind, tournament, stageName, tableData, grade);
                const pageWidthMm = contentWidthMm + MARGIN_MM * 2;
                const pageHeightMm = contentHeightMm + MARGIN_MM * 2;

                // Dynamic import - see the "Interactive PDF" jsPDF import
                // above for why.
                const { default: jsPDF } = await import("jspdf");
                const pdf = new jsPDF({
                    unit: 'mm',
                    format: [pageWidthMm, pageHeightMm],
                    orientation: pageWidthMm >= pageHeightMm ? 'landscape' : 'portrait',
                });
                await registerPdfFonts(pdf);
                drawBlockPage(pdf, MARGIN_MM, MARGIN_MM, printableKind, tournament, stageName, tableData, grade, i18n, contentWidthMm, assets);

                const fileName = `${t(BLOCKS[blockKey].label)}_${tournament?.name || tournamentId}`.replace(/[\s\/:*?"<>|]/g, "_");
                pdf.save(`${fileName}.pdf`);
                toast.success(`${t(BLOCKS[blockKey].label)} ${t("saved!")}`);
            }
        } catch (e) {
            console.error("Tabular export error:", e);
            toast.error(t("Export failed"));
        } finally {
            setLoadingKey(null);
            setPrintableData(null);
        }
    };

    return (
        <CustomModal isOpen={isOpen} onClose={onClose}>
            <form className="admin-form" style={{ width: "100%", boxSizing: "border-box" }}>
            <div className="modal-sticky-header">
                <h2><FeatherIcon icon="download" /> {t("Export data")}</h2>
            </div>

                <div
                    className={`admin-card ${loadingKey === 'interactive-pdf' ? "selected" : "editable"}`}
                    style={{
                        opacity: (loadingKey && loadingKey !== 'interactive-pdf') || !stagesInfo ? 0.5 : 1,
                        cursor: (loadingKey || !stagesInfo) ? "wait" : "pointer",
                        gridTemplateColumns: "20px 1fr auto",
                        marginBottom: "14px",
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        if (loadingKey || !stagesInfo) return;
                        handleInteractivePdfExport();
                    }}
                >
                    <div><FeatherIcon icon="book-open" size={16} /></div>
                    <div>
                        <div>{t("Interactive PDF")}</div>
                        <div style={{ fontSize: "11px", color: "var(--secondary-color)", fontWeight: 400, marginTop: "2px" }}>
                            {t("One file with a clickable table of contents and bookmarks")}
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {loadingKey === 'interactive-pdf' ? <FeatherIcon icon="loader" className="spin" size={16} /> : <FeatherIcon icon="download" size={16} />}
                    </div>
                </div>

                <div className="admin-list admin-share">
                        {Object.entries(BLOCKS).map(([key, value]) => {
                            const arr = value.arrayKey && stagesInfo ? (stagesInfo[value.arrayKey] || []) : [];
                            const hasSubStages = !!value.arrayKey;
                            const count = hasSubStages ? arr.length : 1;
                            const isExpanded = expandedBlocks.has(key);
                            const isLoadingParent = loadingKey && loadingKey.startsWith(`tabular-${key}`);
                            // The merged "Draw" block interleaves groups and brackets (see
                            // BLOCKS.draws above) - a shared "Stage N" fallback label counted
                            // across BOTH kinds combined mislabels which group/bracket stage a
                            // position actually is (Groups -> Bracket -> Groups: the 2nd real
                            // group stage would read "Stage 3", not "Stage 2" - picking by that
                            // number then targets someone else's stage entirely). Count each
                            // kind's own position separately instead.
                            const kindCounters = {};
                            const numByIndex = arr.map(it => {
                                const kind = (typeof it === 'object' && it.kind) || 'default';
                                kindCounters[kind] = (kindCounters[kind] || 0) + 1;
                                return kindCounters[kind];
                            });

                            return (
                                <React.Fragment key={key}>
                                    <div
                                        className={`admin-card ${isLoadingParent ? "selected" : "editable"}`}
                                        style={{
                                            opacity: (loadingKey && !isLoadingParent) || !stagesInfo ? 0.5 : 1,
                                            cursor: loadingKey || !stagesInfo ? "wait" : "pointer",
                                            gridTemplateColumns: "20px 1fr 20px"
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (loadingKey || !stagesInfo) return;
                                            setExpandedBlocks(prev => {
                                                const next = new Set(prev);
                                                if (next.has(key)) next.delete(key); else next.add(key);
                                                return next;
                                            });
                                        }}
                                    >
                                        <div><FeatherIcon icon={value.icon} size={16} /></div>
                                        <div>{t(value.label)}</div>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                            {isLoadingParent ? (
                                                <FeatherIcon icon="loader" className="spin" size={16} />
                                            ) : !isExpanded ? (
                                                <FeatherIcon icon="chevron-down" size={16} />
                                            ) : (
                                                <FeatherIcon icon="chevron-up" size={16} />
                                            )}
                                        </div>
                                    </div>

                                    {isExpanded && hasSubStages && count === 0 && (
                                        <div style={{ padding: "10px 20px", fontSize: "13px", color: "var(--secondary-color)", fontStyle: "italic" }}>
                                            {t("No stages created yet")}
                                        </div>
                                    )}

                                    {isExpanded && count === 1 && (
                                        <TabularExportButtons
                                            blockKey={key}
                                            subItemVal={hasSubStages ? arr[0] : 1}
                                            handleTabularExport={handleTabularExport}
                                            loadingKey={loadingKey}
                                        />
                                    )}

                                    {isExpanded && count > 1 && (
                                        <div className="admin-list" style={{ minWidth: "min-content", width: "100%", margin: 0 }}>
                                            {arr.map((item, index) => {
                                                const num = numByIndex[index];
                                                let displayLabel = "";

                                                const exportVal = typeof item === 'object' ? item : num;
                                                const itemKeyId = typeof item === 'object' ? `${item.stage || item.id}_${item.tab || ''}` : num;

                                                if (typeof item === 'object' && item.label) {
                                                    displayLabel = item.label;
                                                } else if (typeof item === 'object' && item.name) {
                                                    displayLabel = item.name;
                                                } else if (typeof item === 'object' && item.date) {
                                                    displayLabel = item.date;
                                                } else {
                                                    displayLabel = `${t('Stage')} ${num}`;
                                                }

                                                // No collapse/expand toggle here - every stage's export
                                                // buttons just sit under its own label right away, same
                                                // as the top-level blocks above.
                                                return (
                                                    <React.Fragment key={itemKeyId}>
                                                        <div
                                                            className="admin-card"
                                                            style={{ gridTemplateColumns: "20px 1fr", marginLeft: "20px", padding: "8px 12px", cursor: "default" }}
                                                        >
                                                            <div><FeatherIcon icon={value.icon} size={14} style={{ opacity: 0.7 }} /></div>
                                                            <div>{displayLabel}</div>
                                                        </div>

                                                        <div style={{ marginLeft: "20px" }}>
                                                            <TabularExportButtons
                                                                blockKey={key}
                                                                subItemVal={exportVal}
                                                                handleTabularExport={handleTabularExport}
                                                                loadingKey={loadingKey}
                                                            />
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>

                <div style={{ fontSize: "12px", color: "var(--secondary-color)", marginTop: "14px" }}>
                    {t("Structured printable tables without web layout. Perfect for PDF, PNG, Excel and Google Docs/Sheets.")}
                </div>
            </form>

            {/* Offscreen container for Tabular PDF/PNG capture */}
            <div
                style={{
                    position: "fixed",
                    top: "0",
                    left: "-9999px",
                    width: "1100px",
                    zIndex: -9999,
                    pointerEvents: "none",
                    opacity: 1,
                    background: "#ffffff"
                }}
            >
                {/* Only ever mounted for the PNG Image export now - the PDF
                    paths (single-block and Interactive PDF) are fully
                    vector (pdfPageComposer.js) and never touch the DOM. */}
                {printableData && (
                    <div ref={printableContainerRef} style={{ width: "100%", background: "#ffffff", padding: "10px" }}>
                        <TournamentPrintableTable
                            tournament={tournament}
                            blockKey={printableData.blockKey}
                            title={printableData.title}
                            subTitle={printableData.subTitle}
                            data={printableData.data}
                            grade={printableData.grade}
                            i18n={i18n}
                        />
                    </div>
                )}
            </div>
        </CustomModal>
    );
};

export default TournamentExportModal;
